<template>
  <div class="d-leaderboard-feed">
    <header class="no-border-radius">
      <div class="wrapper">
        <div class="grid-5">
          <div class="grid-item-3">
            <div class="header-content hidden-until-md">
              <h1>Reviewer Leaderboard</h1>
              <p class="large">
                Discover new reviewers to follow
              </p>
            </div>
            <div class="visible-until-sm space-top-2em space-bottom-2em">
              <h1>Reviewer Leaderboard</h1>
              <p class="large">
                Discover new reviewers to follow
              </p>
            </div>
          </div>
        </div>
        <span class="map" />
      </div>
    </header>

    <section
      ref="leaderboardSection"
      class="section-grey section-thin"
    >
      <div class="wrapper">
        <div class="leaderboard-container">
          <DLeaderboardFeedSearch />

          <div
            class="reviewers-container panel panel-overflow loadable"
            :class="{ 'loading': reviewersSearch.isSearching }"
          >
            <DuiLoadingIndicator
              v-if="reviewersSearch.isSearching"
              size="lg"
            />

            <div class="loaded-content">
              <div class="reviewers-list">
                <!-- Feed header -->
                <div
                  class="feed-header grid-flex grid-no-response border-bottom"
                >
                  <div class="grid-item">
                    <p class="large">
                      <strong v-if="selectedGenreName">{{ selectedGenreName }}:</strong>
                      <span>
                        Top 10 reviewers
                      </span>
                      <strong>{{ selectedSortName }}</strong>
                    </p>
                  </div>
                </div>
                <!-- END Feed header -->

                <div
                  v-if="shouldShowEmptyDataPanel()"
                  class="empty-data-panel space-top-2em space-bottom-2em"
                >
                  We're building the leaderboard!
                  Come back in {{ getDaysToUpdateText() }} to see the results.
                  In the meantime, why not look
                  at <a @click="setPreviousMonth">{{ getPreviousMonthName() }}'s leaderboard</a>?
                </div>

                <!-- Tags and no. of results -->
                <div
                  v-if="(storeDisplayedSearchTags && storeDisplayedSearchTags.length && !shouldShowEmptyDataPanel())"
                  class="space-top-2em"
                >
                  <DSearchTags store-search-prefix="LeaderboardFeed/ReviewerSearch" />
                </div>
                <!-- END Tags and no. of results -->

                <!-- No results -->
                <div
                  v-if="!reviewersSearch.results.length || shouldShowEmptyDataPanel()"
                  class="no-results-with-vessels"
                >
                  <img
                    v-for="num in leaderboardVesselNum"
                    :key="num"
                    alt="no-results"
                    :src="leaderboardVesselPath"
                    title="No results"
                  >
                </div>
                <!-- END No results -->
              </div>

              <div v-if="reviewersSearch.results.length && !shouldShowEmptyDataPanel()">
                <DLeaderboardFeedEntry
                  v-for="(entry) in reviewersSearch.results"
                  :key="entry.id"
                  :entry="entry"
                  :is-user-signed-in="isUserSignedIn"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Vue from 'vue';

import DLeaderboardFeedSearch from './d-leaderboard-feed-search/d-leaderboard-feed-search.vue';
import DLeaderboardFeedEntry from './d-leaderboard-feed-entry/d-leaderboard-feed-entry.vue';
import DSearchTags from '@d-components/d-search-tags/d-search-tags.vue';
import leaderboardService from '@d-services/reviewer-search-service/leaderboard-search-service';
import { getErrorMessage } from '@d-shared/utils/errors';
import { MONTHS } from '@d-shared/config';

export default {
  components: {
    DLeaderboardFeedSearch,
    DLeaderboardFeedEntry,
    DSearchTags,
  },
  props: {
    isUserSignedIn: {
      type: Boolean,
      required: true,
    },
    leaderboardVesselPath: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    reviewersSearch: {
      isSearching: false,
      results: [],
    },
    leaderboardVesselNum: 3,
  }),
  computed: {
    selectedGenreName() {
      const genre = this.$store.getters['LeaderboardFeed/get-selected-genre'];
      if (!genre || !genre.value) {
        return null;
      }

      return genre.text;
    },
    storeDisplayedSearchTags() {
      return this.$store.getters['LeaderboardFeed/ReviewerSearch/get-displayed-search-tags'];
    },
    selectedDate() {
      const date = this.$store.getters['LeaderboardFeed/ReviewerSearch/get-date-tag'];
      return date && date.value.split('-') || [];
    },
    selectedSortBy() {
      return this.$store.getters['LeaderboardFeed/ReviewerSearch/get-sort-by-tag'];
    },
    selectedSortName() {
      const sortBy = this.$store.getters['LeaderboardFeed/ReviewerSearch/get-sort-by-tag'];
      const [ month, year ] = this.selectedDate;
      const today = new Date();

      const sortByTexts = {
        overall: 'overall',
        year: year === today.getFullYear().toString() ? 'this year' : year,
        month: this.isCurrentMonth() ? 'this month' : `${MONTHS[month - 1] || ''} ${year || ''}`,
      };

      return sortByTexts[sortBy && sortBy.value] || sortByTexts.month;
    },
  },
  mounted() {
    this.loadReviewers();
  },
  methods: {
    async loadReviewers() {
      try {
        this.reviewersSearch = await leaderboardService.getReviewers();
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }
    },
    setPreviousMonth() {
      const today = new Date();
      const previousMonth = `${today.getMonth()}-${today.getFullYear()}`;
      const dateSearchTag = {
        hidden: true,
        key: 'date',
        value: previousMonth,
      };

      this.$store.dispatch('LeaderboardFeed/ReviewerSearch/addSearchTagAndSearch', dateSearchTag);
    },
    isMonthView() {
      // It should be undefined, because for the «month» value we remove this searchTag from the store
      return !this.selectedSortBy;
    },
    isFirstWeekOfTheMonth() {
      return this.countDaysToUpdate() > 0;
    },
    isCurrentMonth() {
      const [ month, year ] = this.selectedDate;
      const today = new Date();
      const isCurrentYear = today.getFullYear().toString() === year;

      return isCurrentYear && month === (today.getMonth() + 1).toString();
    },
    shouldShowEmptyDataPanel() {
      return this.isFirstWeekOfTheMonth() && this.isCurrentMonth() && this.isMonthView();
    },
    countDaysToUpdate() {
      const curDate = new Date();
      return 8 - curDate.getDate();
    },
    getDaysToUpdateText() {
      const daysToUpdate = this.countDaysToUpdate();
      return daysToUpdate === 7 ?
        'a week' :
        `${daysToUpdate} ${this.$options.filters.duiPluralize(daysToUpdate, 'day', 'days')}`;
    },
    getPreviousMonthName() {
      const [ month ] = this.selectedDate;

      return MONTHS[month - 2];
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "@dui-styles/base";

  .d-leaderboard-feed {
    background-color: getColor($white);

    header {
      background: transparent;
      overflow: hidden;
      position: relative;

      .map {
        background: url('../../../../assets/directory.png') no-repeat;
        background-position: center right;
        background-size: 100%;
        bottom: -17px;
        display: none;
        height: calc(100% + 48px);
        position: absolute;
        right: 0;
        width: 55%;
        z-index: 0;

        @include screenMoreThan(sm) {
          display: block;
          min-height: 100%;
        }

        @include screenMoreThan(md) {
          height: calc(100% + 48px);
        }
      }
    }

    .leaderboard-container {
      display: grid;
      grid-row-gap: $_3em;
      grid-template-areas:
        "leaderboard-filter"
        "reviewers-container";

      .leaderboard-filter {
        grid-area: leaderboard-filter;
        min-width: 0;
      }

      .reviewers-container {
        grid-area: reviewers-container;
        padding-top: $_1em;

        .feed-header {
          p {
            margin: $_2em $_2em 0 0;

            @include screenMoreThan(xs) {
              margin-bottom: $_2em;
            }
          }
        }
      }

      @include screenMoreThan(sm) {
        grid-column-gap: $_8em;
        grid-row-gap: 0;
        grid-template-columns: repeat(3, 1fr);
        grid-template-areas:
          "leaderboard-filter reviewers-container reviewers-container";
      }
    }
  }
</style>
